import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import Leftmenu from '../Components/Leftmenu';
import Dashnav from '../Components/Dashnav';
import AdmissionTopNotice from '../Components/AdmissionTopNotice';
import './dash.css';
import './ResponsiveDash.css';
import {
  getInterviewJoinAvailability,
  getInterviewPortalPath,
  INTERVIEW_JOIN_EARLY_WINDOW_MINUTES
} from '../utils/interviewScheduling';

const DAILY_API_KEY = process.env.REACT_APP_DAILY_API_KEY || '#';

const getDateObject = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate;
};

const formatInterviewDateTime = (value, fallback = 'Not scheduled') => {
  const dateObj = getDateObject(value);
  if (!dateObj) return fallback;

  return dateObj.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Africa/Nairobi',
    timeZoneName: 'short'
  });
};

const normalizeValue = (value) => String(value || '').trim().toLowerCase();

const matchesCurrentStudent = (record, user) => {
  if (!record || !user) return false;

  return String(record.userId || '') === String(user.uid || '')
    || normalizeValue(record.studentEmail) === normalizeValue(user.email);
};

const getStatusLabel = (status) => {
  if (status === 'active') return 'Interview in progress';
  if (status === 'scheduled') return 'Scheduled interview';
  if (status === 'ended') return 'Interview ended';
  return 'Interview ready';
};

function Interviews() {
  const { meetingId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const auth = getAuth();
  const [authReady, setAuthReady] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [meetingRecord, setMeetingRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(true);
  const [joining, setJoining] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const leftMeeting = searchParams.get('left') === '1';
  const loginRedirectPath = getInterviewPortalPath(meetingId);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
      setAuthReady(true);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const loadMeetingRecord = async () => {
      if (!authReady) {
        return;
      }

      if (!currentUser) {
        setMeetingRecord(null);
        setMeetingUrl('');
        setError('');
        setLoadingRecord(false);
        return;
      }

      setLoadingRecord(true);
      setMeetingUrl('');
      setError('');

      try {
        let matchedRecord = null;

        if (meetingId) {
          const byMeetingId = await getDocs(query(collection(db, 'interviewCalls'), where('meetingId', '==', meetingId)));
          if (!byMeetingId.empty) {
            matchedRecord = { id: byMeetingId.docs[0].id, ...byMeetingId.docs[0].data() };
          } else {
            const byRoomName = await getDocs(query(collection(db, 'interviewCalls'), where('roomName', '==', meetingId)));
            if (!byRoomName.empty) {
              matchedRecord = { id: byRoomName.docs[0].id, ...byRoomName.docs[0].data() };
            }
          }
        } else {
          const directRecord = await getDoc(doc(db, 'interviewCalls', currentUser.uid));
          if (directRecord.exists()) {
            matchedRecord = { id: directRecord.id, ...directRecord.data() };
          } else if (currentUser.email) {
            const byStudentEmail = await getDocs(query(collection(db, 'interviewCalls'), where('studentEmail', '==', currentUser.email)));
            if (!byStudentEmail.empty) {
              matchedRecord = { id: byStudentEmail.docs[0].id, ...byStudentEmail.docs[0].data() };
            }
          }
        }

        if (!matchedRecord) {
          setMeetingRecord(null);
          setError(meetingId ? 'We could not find an interview for this link.' : '');
          return;
        }

        if (!matchesCurrentStudent(matchedRecord, currentUser)) {
          setMeetingRecord(null);
          setError('This interview invite belongs to a different student account. Please sign in with the email you used during registration.');
          return;
        }

        setMeetingRecord(matchedRecord);
      } catch (loadError) {
        console.error('Failed to load interview record:', loadError);
        setMeetingRecord(null);
        setError('Unable to load your interview right now. Please try again in a moment.');
      } finally {
        setLoadingRecord(false);
      }
    };

    loadMeetingRecord();
  }, [authReady, currentUser, meetingId]);

  const joinAvailability = useMemo(() => (
    meetingRecord?.status === 'scheduled'
      ? getInterviewJoinAvailability(meetingRecord?.scheduledFor, currentTime)
      : { canJoin: true, availableAt: null }
  ), [currentTime, meetingRecord]);

  const createParticipantMeetingUrl = useCallback(async (record) => {
    if (!record?.roomName || !record?.roomUrl) {
      return '';
    }

    const redirectUrl = typeof window !== 'undefined'
      ? `${window.location.origin}${getInterviewPortalPath(record.meetingId || record.roomName)}?left=1`
      : undefined;

    const response = await fetch('https://api.daily.co/v1/meeting-tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${DAILY_API_KEY}`
      },
      body: JSON.stringify({
        properties: {
          room_name: record.roomName,
          is_owner: false,
          user_name: currentUser?.displayName || record.studentName || currentUser?.email || 'Student',
          user_id: currentUser?.uid || record.userId || record.roomName,
          enable_prejoin_ui: true,
          redirect_on_meeting_exit: redirectUrl
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Daily meeting token creation failed with status ${response.status}`);
    }

    const tokenData = await response.json();
    if (!tokenData?.token) {
      return record.roomUrl;
    }

    return `${record.roomUrl}${record.roomUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(tokenData.token)}`;
  }, [currentUser]);

  const handleJoinInterview = useCallback(async () => {
    if (!meetingRecord) {
      return;
    }

    if (meetingRecord.status === 'scheduled' && !joinAvailability.canJoin) {
      return;
    }

    setJoining(true);

    try {
      const tokenizedUrl = await createParticipantMeetingUrl(meetingRecord);
      setMeetingUrl(tokenizedUrl || meetingRecord.roomUrl || '');

      const now = Timestamp.now();
      await setDoc(doc(db, 'interviewCalls', meetingRecord.id), {
        status: 'active',
        startedAt: meetingRecord.startedAt || now,
        studentJoinedAt: meetingRecord.studentJoinedAt || now,
        studentJoinedBy: currentUser?.uid || null,
        studentJoinedName: meetingRecord.studentName || currentUser?.displayName || 'Student',
        studentJoinedEmail: currentUser?.email || meetingRecord.studentEmail || '',
        bothJoinedAt: meetingRecord.bothJoinedAt || (meetingRecord?.hostJoinedAt ? now : null),
        lastJoinedAt: now,
        lastJoinedBy: currentUser?.uid || null,
        updatedAt: now
      }, { merge: true });

      setMeetingRecord((prev) => (
        prev ? {
          ...prev,
          status: 'active',
          startedAt: prev.startedAt || now,
          studentJoinedAt: prev.studentJoinedAt || now,
          studentJoinedBy: currentUser?.uid || null,
          studentJoinedName: prev.studentName || currentUser?.displayName || 'Student',
          studentJoinedEmail: currentUser?.email || prev.studentEmail || '',
          bothJoinedAt: prev.bothJoinedAt || (prev?.hostJoinedAt ? now : null),
          lastJoinedAt: now,
          updatedAt: now
        } : prev
      ));
    } catch (joinError) {
      console.error('Failed to prepare interview join link:', joinError);
      setMeetingUrl(meetingRecord.roomUrl || '');
    } finally {
      setJoining(false);
    }
  }, [createParticipantMeetingUrl, currentUser, joinAvailability.canJoin, meetingRecord]);

  if (!authReady) {
    return (
      <div className="studentInterviewGate">
        <div className="studentInterviewGateCard">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="studentInterviewGate">
        <div className="studentInterviewGateCard">
          <p className="studentInterviewEyebrow">Ubunifu College interview</p>
          <h1>Sign in to open your interview</h1>
          <p>Use the same email address and password you set during registration. If you cannot remember your password, reset it and we will send you a password reset link.</p>
          <div className="studentInterviewGateActions">
            <Link to={`/login?redirect=${encodeURIComponent(loginRedirectPath)}`} className="studentInterviewPrimaryBtn">
              Sign in
            </Link>
            <Link to="/recover" className="studentInterviewSecondaryBtn">
              Reset password
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className='mainContainer'>
        <Leftmenu />
        <div className='theMainContainer'>
          <Dashnav />
          <AdmissionTopNotice />
          <div className="studentInterviewPage">
            <div className="studentInterviewShell">
              {loadingRecord ? (
                <div className="studentInterviewCard studentInterviewCard--state studentInterviewCard--centered">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : error ? (
                <div className="studentInterviewCard studentInterviewCard--state studentInterviewCard--empty">
                  <p className="studentInterviewEyebrow">Interview access</p>
                  <h2>We could not open this interview</h2>
                  <p>{error}</p>
                </div>
              ) : !meetingRecord ? (
                <div className="studentInterviewCard studentInterviewCard--state studentInterviewCard--empty">
                  <p className="studentInterviewEyebrow">Interview access</p>
                  <h2>No interview invite yet</h2>
                  <p>Your interview will appear here after the admissions team schedules it.</p>
                </div>
              ) : (
                <div className="studentInterviewLayout">
                  <div className="studentInterviewCard">
                    <div className="studentInterviewHeader">
                      <div className="studentInterviewHeaderCopy">
                        <p className="studentInterviewEyebrow">Ubunifu College interview</p>
                        <h2>{meetingRecord.studentName || 'Admissions interview'}</h2>
                        <p className="studentInterviewSubtext">
                          {meetingRecord.course || 'Course not set'} • {meetingRecord.nationality || 'Country not set'}
                        </p>
                      </div>
                      <div className="studentInterviewStatusWrap">
                        <span className={`studentInterviewStatus studentInterviewStatus--${meetingRecord.status || 'ready'}`}>
                          {getStatusLabel(meetingRecord.status)}
                        </span>
                      </div>
                    </div>

                    <div className="studentInterviewMetaGrid">
                      <div className="studentInterviewMetaCard">
                        <span>Meeting ID</span>
                        <strong>{meetingRecord.meetingId || meetingRecord.roomName || 'Not set'}</strong>
                      </div>
                      <div className="studentInterviewMetaCard">
                        <span>Scheduled for</span>
                        <strong>{formatInterviewDateTime(meetingRecord.scheduledFor || meetingRecord.startedAt)}</strong>
                      </div>
                      <div className="studentInterviewMetaCard">
                        <span>Expected end</span>
                        <strong>{formatInterviewDateTime(meetingRecord.scheduledEndsAt, 'To be confirmed')}</strong>
                      </div>
                    </div>

                    {leftMeeting && meetingRecord.status !== 'ended' && (
                      <div className="studentInterviewNotice">
                        You left the meeting. Rejoin any time from this page until the admissions team ends the interview.
                      </div>
                    )}

                    {meetingRecord.status === 'scheduled' && !joinAvailability.canJoin && (
                      <div className="studentInterviewNotice studentInterviewNotice--scheduled">
                        This interview opens {INTERVIEW_JOIN_EARLY_WINDOW_MINUTES} minutes before the scheduled time. You can join at {formatInterviewDateTime(joinAvailability.availableAt)}.
                      </div>
                    )}

                    {meetingRecord.status === 'ended' ? (
                      <div className="studentInterviewCard studentInterviewCard--inner studentInterviewCard--empty">
                        <h3>This interview has ended</h3>
                        <p>Please contact admissions if you need a new invite.</p>
                      </div>
                    ) : meetingUrl && !leftMeeting ? (
                      <div className="studentInterviewFrameWrap">
                        <div className="studentInterviewFrameHeader">
                          <div>
                            <p className="studentInterviewFrameLabel">Interview room</p>
                            <strong>{meetingRecord.studentName || 'Admissions interview'}</strong>
                          </div>
                          <span>{formatInterviewDateTime(meetingRecord.scheduledFor || meetingRecord.startedAt)}</span>
                        </div>
                        <iframe
                          src={meetingUrl}
                          title={`Interview for ${meetingRecord.studentName || 'student'}`}
                          className="studentInterviewFrame"
                          allow="camera; microphone; fullscreen; speaker-selection; display-capture; autoplay; clipboard-write"
                        />
                      </div>
                    ) : (
                      <div className="studentInterviewActionCard">
                        <div className="studentInterviewActionCopy">
                          <p className="studentInterviewEyebrow">Interview access</p>
                          <h3>{meetingRecord.status === 'active' ? 'The room is open' : 'Join from your student account'}</h3>
                          <p>
                            {meetingRecord.status === 'scheduled' && !joinAvailability.canJoin
                              ? 'Please wait until the join window opens.'
                              : 'When you are ready, use the button below to open the interview room.'}
                          </p>
                        </div>
                        {leftMeeting ? (
                          <button
                            type="button"
                            className="studentInterviewPrimaryBtn studentInterviewPrimaryBtn--button"
                            onClick={() => window.location.assign(getInterviewPortalPath(meetingRecord.meetingId || meetingRecord.roomName))}
                          >
                            Rejoin interview
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="studentInterviewPrimaryBtn studentInterviewPrimaryBtn--button"
                            disabled={joining || (meetingRecord.status === 'scheduled' && !joinAvailability.canJoin)}
                            onClick={handleJoinInterview}
                          >
                            {joining ? 'Opening interview...' : 'Join interview'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <aside className="studentInterviewSidebar">
                    <div className="studentInterviewSidebarCard">
                      <p className="studentInterviewEyebrow">Interview notes</p>
                      <h3>What to keep in mind</h3>
                      <div className="studentInterviewChecklist">
                        <p>Find a quiet place with stable internet before the interview starts.</p>
                        <p>Keep your camera and microphone ready so the admissions team can begin on time.</p>
                        <p>If you leave the room, come back to this page to rejoin the same interview.</p>
                      </div>
                    </div>

                    <div className="studentInterviewSidebarCard studentInterviewSidebarCard--muted">
                      <p className="studentInterviewEyebrow">Need help?</p>
                      <h3>Interview support</h3>
                      <p>If the room does not open at the expected time, refresh this page or contact the admissions team for a new invite.</p>
                      <Link to="/dashboard" className="studentInterviewSecondaryBtn studentInterviewSecondaryBtn--block">
                        Back to dashboard
                      </Link>
                    </div>
                  </aside>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Interviews;
